import React from 'react'
import { formatDate, formatTime } from '../../../common/utils/appUtils'
import {
  PAYMENT_MODE_OPTIONS,
  PRODUCT_DETAIL_FIELDS,
} from '../../../common/constant/page-constant'
import currencyFormatter from '../../../common/utils/currencyFormatterUtils'
import { formatProductListLabel } from '../../../common/utils/productUtils'

const CustomerCopyTemplate = ({ invoiceData, branchData }) => {
  const getDiscountAmount = () => Number(invoiceData.discount) || 0

  const getVatAmount = () => Number(invoiceData.vat || 0).toFixed(2)

  const getVatPercentage = () => {
    if (
      invoiceData.vat_percentage !== undefined &&
      invoiceData.vat_percentage !== null
    ) {
      return invoiceData.vat_percentage
    }
    const baseAmount = (invoiceData.amount || 0) - getDiscountAmount()
    if (baseAmount > 0 && invoiceData.vat > 0) {
      return parseFloat(((invoiceData.vat / baseAmount) * 100).toFixed(2))
    }
    return 0
  }

  const getTotalQuantity = () => {
    return (
      invoiceData.product_details?.reduce((sum, product) => {
        return sum + (product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0)
      }, 0) || 0
    )
  }

  const getPaymentModeLabel = () => {
    const mode = PAYMENT_MODE_OPTIONS.find(
      (item) => item.name === invoiceData.payment_mode
    )
    return mode?.label || invoiceData.payment_mode || 'Cash'
  }

  const isOnlineSale = invoiceData.type === 'ONLINE'

  const styles = {
    container: {
      fontFamily: "'Poppins', 'Arial', sans-serif",
      width: '100%',
      margin: '0 auto',
      padding: '5px',
      backgroundColor: 'white',
      fontSize: '13px',
    },
    title: {
      textAlign: 'center',
      fontSize: '14px',
      fontWeight: 'bold',
      marginBottom: '6px',
      borderBottom: '1px solid #000',
      paddingBottom: '4px',
    },
    headerSection: {
      marginBottom: '6px',
      paddingBottom: '6px',
      borderBottom: '1px solid #000',
    },
    headerRow: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '4px',
    },
    logo: {
      width: '32px',
      height: '32px',
      marginRight: '6px',
      objectFit: 'contain',
    },
    branchInfo: {
      flex: 1,
    },
    branchNameRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    branchName: {
      fontSize: '13px',
      fontWeight: 'bold',
    },
    branchPhone: {
      fontSize: '11px',
    },
    branchAddress: {
      fontSize: '11px',
      marginTop: '2px',
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '11px',
      marginTop: '3px',
    },
    customerSection: {
      marginBottom: '6px',
      paddingBottom: '4px',
      borderBottom: '1px solid #000',
    },
    customerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '12px',
      marginBottom: '2px',
    },
    customerLabel: {
      fontWeight: 'bold',
    },
    // Table - match physical receipt: no cell borders, just header underline
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '4px',
    },
    th: {
      borderTop: '1px solid #000',
      borderBottom: '1px solid #000',
      padding: '3px 0',
      textAlign: 'left',
      fontWeight: 'bold',
      fontSize: '12px',
      backgroundColor: 'white',
    },
    thCenter: {
      borderTop: '1px solid #000',
      borderBottom: '1px solid #000',
      padding: '3px 4px',
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: '12px',
      backgroundColor: 'white',
    },
    thRight: {
      borderTop: '1px solid #000',
      borderBottom: '1px solid #000',
      padding: '3px 0px!important',
      textAlign: 'right',
      fontWeight: 'bold',
      fontSize: '12px',
      backgroundColor: 'white',
    },
    thleft: {
      borderTop: '1px solid #000',
      borderBottom: '1px solid #000',
      padding: '3px 0.5px',
      textAlign: 'left',
      fontWeight: 'bold',
      fontSize: '12px',
      backgroundColor: 'white',
    },
    td: {
      padding: '3px 0',
      fontSize: '12px',
      verticalAlign: 'top',
      border: 'none',
    },
    tdCenter: {
      padding: '3px 0.2px',
      textAlign: 'center',
      fontSize: '12px',
      verticalAlign: 'top',
      border: 'none',
    },
    tdleft: {
      padding: '3px 0.5px!important',
      textAlign: 'left',
      fontSize: '12px',
      verticalAlign: 'top',
      border: 'none',
    },
    tdRight: {
      padding: '3px 0px!important',
      textAlign: 'right',
      fontSize: '12px',
      verticalAlign: 'top',
      border: 'none',
    },
    totalRow: {
      fontWeight: 'bold',
      borderTop: '1px solid #000',
      borderBottom: '1px solid #000',
    },
    summarySection: {
      marginTop: '4px',
      paddingTop: '4px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '12px',
      padding: '2px 0',
    },
    netAmountRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '13px',
      fontWeight: 'bold',
      padding: '4px 0',
      borderTop: '1px solid #000',
      marginTop: '3px',
    },
    disclaimer: {
      marginTop: '8px',
      paddingTop: '6px',
      borderTop: '1px solid #000',
      textAlign: 'center',
      fontSize: '11px',
      lineHeight: '1.4',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '11px',
      marginTop: '8px',
      paddingTop: '5px',
      borderTop: '1px solid #000',
    },
  }

  return (
    <div style={styles.container} className="invoice-template customer-copy">
      {/* Title */}
      <div style={styles.title}>SALES INVOICE</div>

      {/* Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerRow}>
          {isOnlineSale ? (
            <div style={styles.branchInfo}>
              <div style={styles.branchNameRow}>
                <span style={styles.branchName}>DG ePharma</span>
                <div style={styles.branchPhone}>📞 09606501155</div>
              </div>
              <div style={styles.branchAddress}>📍 Online Order</div>
            </div>
          ) : (
            <>
              {branchData?.logo && (
                <img src={branchData.logo} alt="logo" style={styles.logo} />
              )}
              <div style={styles.branchInfo}>
                <div style={styles.branchNameRow}>
                  <span style={styles.branchName}>
                    {branchData?.pharmacy_name ||
                      invoiceData?.branch_details?.pharmacy_name ||
                      ''}
                    {branchData?.name || invoiceData?.branch_details?.name
                      ? ` (${branchData?.name || invoiceData?.branch_details?.name})`
                      : ''}
                  </span>
                  <div style={styles.branchPhone}>
                    📞{' '}
                    {branchData?.mobile_no ||
                      invoiceData?.branch_details?.mobile_no}
                  </div>
                </div>
                <div style={styles.branchAddress}>
                  📍{' '}
                  {branchData?.address || invoiceData?.branch_details?.address}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={styles.infoRow}>
          <span>
            <strong>Invoice:</strong> {invoiceData.invoice_number}
          </span>
          <span>
            {' '}
            {formatDate(invoiceData.updated_at || invoiceData.created_at)}
          </span>
        </div>
        <div style={styles.infoRow}>
          <span>
            <strong>Served by:</strong>{' '}
            {invoiceData.served_by || invoiceData.created_by_name || 'Staff'}
          </span>
          <span>
            {' '}
            {formatTime(invoiceData.updated_at || invoiceData.created_at)}
          </span>
        </div>
      </div>

      {/* Customer Section */}
      <div style={styles.customerSection}>
        <div style={styles.customerRow}>
          <span style={styles.customerLabel}>
            {invoiceData?.customer?.name || 'Walk-in Customer'}
          </span>
          <span>📞 {invoiceData?.customer?.mobile_no || '-'}</span>
        </div>
        <div style={styles.customerRow}>
          <span>
            📍{' '}
            {invoiceData?.customer?.shipping_address ||
              invoiceData?.customer?.billing_address ||
              '-'}
          </span>
        </div>
      </div>

      {/* Products Table — matches physical receipt layout */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Sl No.</th>
            <th style={styles.th}>Product Name</th>
            <th style={styles.th}>Category</th>
            <th style={styles.thCenter}>Qty</th>
            <th style={styles.thleft}>Rate/ Unit</th>
            <th style={styles.thRight}>Amt</th>
          </tr>
        </thead>
        <tbody>
          {invoiceData.product_details?.map((product, index) => {
            const qty = product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || 0
            const rate = product?.[PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT] || 0
            const amount = qty * rate
            const productName =
              product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] || ''
            const dose = product?.[PRODUCT_DETAIL_FIELDS.DOSE] || ''
            const strength = product?.strength || ''
            const size = product?.size || ''
            const category =
              product?.category || product?.product_category || ''
            const lineLabel = formatProductListLabel(
              { name: productName, size, dose, strength, category },
              { includeCategory: false }
            )

            return (
              <tr key={index}>
                <td style={styles.td}>{index + 1}</td>

                <td style={styles.td}>{lineLabel}</td>
                <td style={styles.td}>{category}</td>
                <td style={styles.tdCenter}>{qty}</td>
                <td style={styles.tdleft}>{Number(rate).toFixed(2)}</td>
                <td style={styles.tdRight}>{Number(amount).toFixed(2)}</td>
              </tr>
            )
          })}
          {/* Total Row */}
          <tr style={styles.totalRow}>
            <td style={styles.td} colSpan={2}>
              <strong>Total</strong>
            </td>
            <td style={styles.tdCenter}>
              <strong>{getTotalQuantity()}</strong>
            </td>
            <td style={styles.tdleft}></td>
            <td style={styles.tdRight}>
              <strong>{Number(invoiceData.amount || 0).toFixed(2)}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Summary Section */}
      <div style={styles.summarySection}>
        <div style={styles.summaryRow}>
          <span>
            <strong>Amount Due</strong>
          </span>
          <span>{Number(getDiscountAmount() || 0).toFixed(2)}</span>
        </div>
        <div style={styles.summaryRow}>
          <span>
            <strong>VAT</strong>
          </span>
          <span>{Number(0)}</span>
        </div>
        <div style={styles.netAmountRow}>
          <span>Net Amount (BDT)</span>
          <span>{Number(invoiceData.total_amount || 0).toFixed(2)}</span>
        </div>

        <div style={styles.summaryRow}>
          <span>Payment Mode</span>
          <span>{getPaymentModeLabel()}</span>
        </div>

        {invoiceData.payment_mode === 'CASH' && (
          <div style={styles.summaryRow}>
            <span>Paid by Cash</span>
            <span>
              {Number(
                invoiceData.paid_amount || invoiceData.total_amount || 0
              ).toFixed(2)}
            </span>
          </div>
        )}
        {invoiceData.payment_mode === 'CARD' && (
          <>
            {invoiceData.transaction_fee_percent > 0 && (
              <div style={styles.summaryRow}>
                <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                <span>
                  {Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}
                </span>
              </div>
            )}
            {invoiceData.final_card_amount &&
              invoiceData.transaction_fee_amount > 0 && (
                <div style={styles.summaryRow}>
                  <span>Final Card Amt</span>
                  <span>
                    {Number(invoiceData.final_card_amount || 0).toFixed(2)}
                  </span>
                </div>
              )}
            {invoiceData.total_payable &&
              invoiceData.transaction_fee_amount > 0 && (
                <div style={{ ...styles.summaryRow, fontWeight: 'bold' }}>
                  <span>Total Payable</span>
                  <span>
                    {Number(invoiceData.total_payable || 0).toFixed(2)}
                  </span>
                </div>
              )}
          </>
        )}
        {invoiceData.payment_mode === 'ONLINE' && (
          <>
            {invoiceData.mfs_name && (
              <div style={styles.summaryRow}>
                <span>MFS</span>
                <span>{invoiceData.mfs_name}</span>
              </div>
            )}
            {invoiceData.transaction_fee_percent > 0 && (
              <div style={styles.summaryRow}>
                <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                <span>
                  {Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}
                </span>
              </div>
            )}
            {invoiceData.total_payable &&
              invoiceData.transaction_fee_amount > 0 && (
                <div style={{ ...styles.summaryRow, fontWeight: 'bold' }}>
                  <span>Total Payable</span>
                  <span>
                    {Number(invoiceData.total_payable || 0).toFixed(2)}
                  </span>
                </div>
              )}
          </>
        )}
        {invoiceData.payment_mode === 'CASH_CARD' && (
          <>
            <div style={styles.summaryRow}>
              <span>Paid by Cash</span>
              <span>{Number(invoiceData.amount_cash || 0).toFixed(2)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Paid by Card</span>
              <span>{Number(invoiceData.amount_card || 0).toFixed(2)}</span>
            </div>
            {invoiceData.transaction_fee_percent > 0 && (
              <div style={styles.summaryRow}>
                <span>Fee ({invoiceData.transaction_fee_percent}%)</span>
                <span>
                  {Number(invoiceData.transaction_fee_amount || 0).toFixed(2)}
                </span>
              </div>
            )}
            {invoiceData.final_card_amount &&
              invoiceData.transaction_fee_amount > 0 && (
                <div style={styles.summaryRow}>
                  <span>Final Card Amt</span>
                  <span>
                    {Number(invoiceData.final_card_amount || 0).toFixed(2)}
                  </span>
                </div>
              )}
            {invoiceData.total_payable &&
              invoiceData.transaction_fee_amount > 0 && (
                <div style={{ ...styles.summaryRow, fontWeight: 'bold' }}>
                  <span>Total Payable</span>
                  <span>
                    {Number(invoiceData.total_payable || 0).toFixed(2)}
                  </span>
                </div>
              )}
          </>
        )}
        {invoiceData.payment_mode === 'DUE_SALES' && (
          <div style={styles.summaryRow}>
            <span>Due Amount</span>
            <span>
              {invoiceData.payment_status !== 'PENDING'
                ? '0.00'
                : Number(invoiceData.total_amount || 0).toFixed(2)}
            </span>
          </div>
        )}
        {invoiceData.payment_mode === 'DUE_SALES' &&
          invoiceData.payment_status !== 'PENDING' && (
            <>
              <div style={styles.summaryRow}>
                <span>Payment Status:</span>
                <span>Received</span>
              </div>
              <div style={styles.summaryRow}>
                <span>Received Date:</span>
                <span>
                  {invoiceData.updated_at && formatDate(invoiceData.updated_at)}
                </span>
              </div>
              <div style={styles.summaryRow}>
                <span>Received Mode:</span>
                <span>
                  {invoiceData.payment_status === 'PAID_VOUCHER'
                    ? 'Cash in Hand'
                    : ''}
                </span>
              </div>
            </>
          )}
      </div>

      {/* Disclaimer */}
      <div style={styles.disclaimer}>
        {isOnlineSale ? (
          <div>"SOLD ITEMS CAN NOT BE EXCHANGED/RETURNED"</div>
        ) : (
          <div>
            বিঃদ্রঃ সেলস রিসিট ব্যতীত বিক্রিত পণ্য ফেরত বা পরিবর্তন করা হয় না।
            ক্রয়কৃত পণ্য ৭ (সাত) দিনের মধ্যে পরিবর্তনযোগ্য। তবে পাতা কাটা ওষুধ,
            ইন্সুলিন, ভ‍্যাক্সিন এবং সুগার টেস্ট স্ট্রিপ ফেরত বা পরিবর্তনযোগ্য
            নয়।
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <span>Powered by: DG ePharma</span>
        <span>📞 0936846398</span>
      </div>
    </div>
  )
}

export default CustomerCopyTemplate
